import { defineStore } from "pinia";
import { ref } from "vue";
import TcVod from "vod-js-sdk-v6";
import { getVideoUploadSignature } from "@/api/video";

// 视频上传配置接口
export interface VideoUploadConfig {
  onProgress?: (percent: number) => void;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
  onCancel?: () => void;
}

// 视频上传结果接口
export interface VideoUploadResult {
  fileId: string;
  videoUrl: string;
  verifyContent?: string;
  coverUrl?: string;
}

// 视频上传工具类
export class VideoUploader {
  private tcVod: any;
  private uploader: any;

  constructor() {
    // 初始化腾讯云视频上传SDK
    // 根据腾讯云文档，TcVod 可能是一个函数而不是类
    if (typeof TcVod === "function") {
      this.tcVod = new TcVod({
        getSignature: this.getSignature.bind(this),
      });
    } else if (TcVod && typeof TcVod.default === "function") {
      this.tcVod = new TcVod.default({
        getSignature: this.getSignature.bind(this),
      });
    } else {
      // 如果都不是，尝试直接使用
      this.tcVod = TcVod({
        getSignature: this.getSignature.bind(this),
      });
    }
  }

  // 获取上传签名
  private async getSignature(): Promise<string> {
    try {
      const result = await getVideoUploadSignature();
      return result.data.signature;
    } catch (error) {
      console.error("获取视频上传签名失败:", error);
      throw new Error("获取上传签名失败");
    }
  }

  // 上传视频
  async uploadVideo(
    mediaFile: File,
    coverFile?: File,
    verifyContent?: string,
    config?: VideoUploadConfig
  ): Promise<VideoUploadResult> {
    try {
      // 创建上传实例
      this.uploader = this.tcVod.upload({
        mediaFile,
        coverFile,
        // 开启断点续传
        enableResume: true,
        // 开启园区竞速
        enableRaceRegion: true,
        // 分块上传配置
        chunkSize: 8388608, // 8MB
        chunkParallelLimit: 6,
        chunkRetryTimes: 2,
        // 进度回调频率
        progressInterval: 1000,
      });

      // 监听上传进度
      if (config?.onProgress) {
        this.uploader.on("media_progress", (info: any) => {
          config.onProgress!(info.percent);
        });
      }

      // 监听封面上传进度
      if (coverFile && config?.onProgress) {
        this.uploader.on("cover_progress", (info: any) => {
          // 这里可以根据需要处理封面上传进度
          // console.log("封面上传进度:", info.percent);
        });
      }

      // 监听媒体文件上传完成
      this.uploader.on("media_upload", (info: any) => {
        // console.log("视频上传完成:", info);
      });

      // 监听封面上传完成
      if (coverFile) {
        this.uploader.on("cover_upload", (info: any) => {
          // console.log("封面上传完成:", info);
        });
      }

      // 等待上传完成
      const result = await this.uploader.done();

      // 上传成功回调
      if (config?.onSuccess) {
        config.onSuccess(result);
      }

      return {
        fileId: result.fileId,
        videoUrl: result.video.url,
        verifyContent: result.video?.verify_content,
        coverUrl: result.cover?.url,
      };
    } catch (error) {
      console.error("视频上传失败:", error);

      // 上传失败回调
      if (config?.onError) {
        config.onError(error);
      }

      throw error;
    }
  }

  // 取消上传
  cancel(): void {
    if (this.uploader) {
      this.uploader.cancel();
      this.uploader = null;
    }
  }

  // 检查文件类型
  static isValidVideoFile(file: File): boolean {
    const validTypes = [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/wmv",
      "video/flv",
      "video/webm",
      "video/mkv",
      "video/3gp",
    ];
    return validTypes.includes(file.type);
  }

  // 检查文件大小 (默认限制500MB)
  static isValidFileSize(
    file: File,
    maxSize: number = 500 * 1024 * 1024
  ): boolean {
    return file.size <= maxSize;
  }

  // 获取文件大小格式化显示
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }
}

export const useVideoStore = defineStore("video", () => {
  const showVideoModal = ref(false);
  const currentVideoUrl = ref("");

  // 创建视频上传实例
  const videoUploader = new VideoUploader();

  const openVideoModal = (videoUrl: string) => {
    currentVideoUrl.value = videoUrl;
    showVideoModal.value = true;
  };

  const closeVideoModal = () => {
    showVideoModal.value = false;
    currentVideoUrl.value = "";
  };

  return {
    showVideoModal,
    currentVideoUrl,
    openVideoModal,
    closeVideoModal,
    videoUploader,
  };
});
